import React, { useContext, useState } from 'react';
import {
    ColDef,
    FirstDataRenderedEvent,
    GridApi,
    GridReadyEvent,
} from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { translateBlockValue } from 'i18n';

import {
    BudgetProposalT,
    BudgetVentilatedDataT,
    BudgetVentilatedDataWithCadrageT,
} from 'routes/BudgetModule/config/types';
import { styled } from '@mui/material/styles';
import { Box, lighten } from '@mui/material';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import NumberFloatingFilter from 'ui/AgGridReact/NumberFloatingFilter';
import ChipCellRenderer from 'ui/AgGridReact/CellRenderers/ChipCellRenderer';
import { ButtonMenu } from 'components/ButtonMenu';
import {
    useExportInscriptionsExcel,
    useExportInscriptionsExcelForReimport,
} from 'routes/BudgetModule/helpers/export';
import GetAppIcon from '@mui/icons-material/GetApp';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { BudgetProposalVentilationBy } from 'routes/BudgetModule/config/queries';
import { useGetIntegrations } from 'helpers/integrations';
import { BudgetBys } from 'business/constants';
import {
    GESTIONNAIRE_SOFTWARE_ID,
    ORG_CHART_FINANCE_SOFTWARE_ID,
} from 'routes/BudgetModule/config/constants';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import ButtonLinkCellRenderer from './ButtonLinkCellRenderer';
import LinkCellRenderer from './LinkCellRenderer';
import { getColDefs } from '../colDefinitions';
import { LockerCellRenderer } from './cellRendererLock';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { useGetPhases } from 'routes/BudgetModule/shared/phases/hook';
import { getPhaseProjectionsAvailable } from 'routes/BudgetModule/shared/phases/phase';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

type Props = {
    data: BudgetVentilatedDataWithCadrageT[] | BudgetVentilatedDataT[];
    budgetProposal: BudgetProposalT;
    gridTitle: string;
    inscriptionsData: { columns: ResponseColumnT[]; data: InscriptionT[] };
    ventilationBy: BudgetProposalVentilationBy;
    hideCanEditColumn: boolean;
};

const StyGrid = styled(Box, {
    name: 'StyGrid',
})({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: '100%',
    flexGrow: 1,
    backgroundColor: 'white',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
});

const StyEnabledChip = styled(Chip, {
    name: 'StyEnabledChip',
})(({ theme }) => ({
    marginLeft: '1rem',
    color: theme.palette.green,
    borderColor: theme.palette.green,
    backgroundColor: lighten(theme.palette.green!, 0.8),
    fontWeight: 'bold',
}));

const StyHeader = styled(Box, {
    name: 'StyHeader',
})({
    display: 'flex',
    width: '100%',
    flex: 1,
    margin: '16px',
});

const StyGridTitleContainer = styled(Box, {
    name: 'StyGridTitleContainer',
})(({ theme }) => ({
    display: 'flex',
    alignSelf: 'center',
    flex: 1,
    alignItems: 'center',
    color: theme.palette.primary.main,
    marginLeft: '48px',
}));

const StyButtonContainer = styled(Box, {
    name: 'StyButtonContainer',
})(() => ({
    display: 'flex',
    alignItems: 'center',
    marginLeft: '1rem',
    marginRight: '1rem',
}));

const StyExportButton = styled(ButtonMenu, {
    name: 'StyExportButton',
})({
    height: '26px',

    minHeight: '26px',
});

export default function Grid({
    data,
    budgetProposal,
    gridTitle,
    inscriptionsData,
    ventilationBy,
    hideCanEditColumn,
}: Props): React.ReactElement {
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const { t } = useTranslation();
    const { projects } = useGetProjects();
    const statusesSettings = useContext(StatusSettingsContext);
    const { data: phases = [] } = useGetPhases();

    const { data: integrations } = useGetIntegrations();
    const columns = getColDefs(budgetProposal, ventilationBy);

    const onGridReady = (params: GridReadyEvent) => {
        setGridApi(params.api);
    };

    const onFirstDataRendered = (params: FirstDataRenderedEvent) => {
        params.api.sizeColumnsToFit();
    };

    const exportGridContent = () => {
        gridApi?.exportDataAsExcel({ fileName: gridTitle });
    };

    const exportInscriptionsExcel = useExportInscriptionsExcel({
        inscriptionsData,
        phase: budgetProposal.phase,
        statusesSettings,
        projects,
        exercice: budgetProposal.exercice,
    });

    const secondaryBudgetAxisSourceTable =
        useSelectSecondaryBudgetAxisSourceTable();

    const exportInscriptionsExcelForReimport =
        useExportInscriptionsExcelForReimport({
            inscriptionsData,
            phase: budgetProposal.phase,
            statusesSettings,
            exercice: budgetProposal.exercice,
            secondaryBudgetAxisSourceTable,
        });

    const exportMenuItems = [
        {
            name: t('budgetProposal.exportInscriptionsExcel'),
            action: exportInscriptionsExcel,
        },
        {
            name: t('budgetProposal.exportInscriptiontForReimport'),
            action: exportInscriptionsExcelForReimport,
        },
        {
            name: t('budgetProposal.exportBudgetLines'),
            action: exportGridContent,
        },
    ];

    const projectionsAvailableChip = (
        <StyEnabledChip
            variant="outlined"
            label={t('budgetProposal.projectionsAvailable')}
        />
    );

    const colDefs: ColDef[] = columns.map((column) => ({
        ...column,
        headerName: translateBlockValue(column.field!, true),
        resizable: true,
        suppressFloatingFilterButton: true,
        menuTabs: [
            'filterMenuTab' as const,
            'generalMenuTab' as const,
            'columnsMenuTab' as const,
        ],
    }));
    const updateStatusColDef = {
        headerName: t('budgetProposal.update'),
        field:
            budgetProposal.by === BudgetBys.GESTIONNAIRE
                ? GESTIONNAIRE_SOFTWARE_ID
                : ORG_CHART_FINANCE_SOFTWARE_ID,
        cellRenderer: LockerCellRenderer,
        suppressSizeToFit: true,
        sortable: false,
        width: 90,
        cellRendererParams: {
            by: budgetProposal.by,
            rights: budgetProposal.canResourceEdit,
        },
    };

    if (!integrations) {
        return <></>;
    }

    const columnDefs = hideCanEditColumn
        ? colDefs
        : [updateStatusColDef, ...colDefs];

    return (
        <StyGrid>
            <StyHeader>
                <StyGridTitleContainer>
                    <Typography variant="h5">{gridTitle}</Typography>
                    {getPhaseProjectionsAvailable(phases, budgetProposal.phase)
                        ? projectionsAvailableChip
                        : null}
                </StyGridTitleContainer>
                <StyButtonContainer>
                    <StyExportButton
                        iconElement={<GetAppIcon />}
                        buttonLabel={t('budgetProposal.export')}
                        menuItems={exportMenuItems}
                        extra={{
                            variant: 'contained',
                            color: 'primary',
                        }}
                    />
                </StyButtonContainer>
            </StyHeader>
            <div
                className="ag-theme-material"
                style={{ width: '100%', height: '750px' }}
            >
                <AgGridReact
                    rowData={data}
                    defaultColDef={{
                        floatingFilter: true,
                    }}
                    columnDefs={columnDefs}
                    components={{
                        linkCellRenderer: LinkCellRenderer,
                        buttonLinkCellRenderer: ButtonLinkCellRenderer,
                        chipCellRenderer: ChipCellRenderer,
                        textFloatingFilter: TextFloatingFilter,
                        numberFloatingFilter: NumberFloatingFilter,
                    }}
                    onFirstDataRendered={onFirstDataRendered}
                    onGridReady={onGridReady}
                />
            </div>
        </StyGrid>
    );
}
